import { WorkspaceDetailsDto } from "@common/dto/workspace/workspace-details.dto";
import {
    clientSchema,
    ClientSchema,
    ClientsQueryParams,
    GetWorkspacesQueryParams,
} from "@common/schemas/client.schema";
import {
    ClientsCloseOverviewDetailsQuery,
    ClientsCloseOverviewSlimQuery,
    CloseUnreadTaskCountsQuery,
} from "@common/schemas/clientsCloseOverview.schema";
import {
    QueryClient,
    queryOptions,
    useMutation,
    useQuery,
} from "@tanstack/react-query";
import { keepPreviousDataOnPageChange } from "./keepPreviousDataOnPageChange";
import { workspacesContract } from "@common/contracts/workspaces.contract";
import {
    UpdateWorkspacePreferencesBody,
    WorkspacesListQuery,
} from "@common/schemas/workspace.schema";
import { Workspace, WorkspaceDetails } from "@common/types/workspace";
import { queryClient } from "queryClient";
import { getBackendAPIClient } from "@lib/backendAPIClient";
import { accountingReportsQueryKeys } from "./accountingReports.api";

export const workspaceQueryKeys = {
    all: () => ["workspace"],
    allQueries: () => [...workspaceQueryKeys.all(), "queries"],
    list: (query?: WorkspacesListQuery) => [
        ...workspaceQueryKeys.all(),
        "list",
        query,
    ],
    query: (params?: GetWorkspacesQueryParams) => [
        ...workspaceQueryKeys.allQueries(),
        params,
    ],
    details: (workspaceId: string) => [
        ...workspaceQueryKeys.all(),
        "details",
        workspaceId,
    ],
    clientsQuery: (params?: GetWorkspacesQueryParams) => [
        ...workspaceQueryKeys.allQueries(),
        "clients",
        params,
    ],
    allClientsForOrgQuery: (organizationId: string) => [
        ...workspaceQueryKeys.allQueries(),
        "allClientsForOrg",
        organizationId,
    ],
    clientsCloseOverviewSlim: () => [
        ...workspaceQueryKeys.allQueries(),
        "clientsCloseOverviewSlim",
    ],
    clientsCloseOverviewSlimQuery: (params?: ClientsCloseOverviewSlimQuery) => [
        ...workspaceQueryKeys.clientsCloseOverviewSlim(),
        params,
    ],
    clientsCloseOverviewDetailsQuery: (
        params?: ClientsCloseOverviewDetailsQuery,
    ) => [...workspaceQueryKeys.clientsCloseOverviewDetails(), params],
    clientsCloseOverviewDetails: () => [
        ...workspaceQueryKeys.allQueries(),
        "clientsCloseOverviewDetails",
    ],
    closeUnreadCounts: () => [
        ...workspaceQueryKeys.allQueries(),
        "closeUnreadCounts",
    ],
    closeUnreadCountsQuery: (params?: CloseUnreadTaskCountsQuery) => [
        ...workspaceQueryKeys.closeUnreadCounts(),
        params,
    ],
} as const;

export function invalidateClientsCloseOverviewQueries(client: QueryClient) {
    client.invalidateQueries({
        queryKey: workspaceQueryKeys.clientsCloseOverviewSlim(),
        exact: false,
    });
    client.invalidateQueries({
        queryKey: workspaceQueryKeys.clientsCloseOverviewDetails(),
        exact: false,
    });
}

const workspaceContractClient = getBackendAPIClient(workspacesContract);

export async function fetchWorkspacesList(query?: WorkspacesListQuery) {
    const response = await workspaceContractClient.getWorkspacesList({ query });
    return response.body.data;
}

export function useWorkspacesList(
    query?: WorkspacesListQuery,
    options?: { enabled?: boolean },
) {
    return useQuery({
        queryKey: workspaceQueryKeys.list(query),
        queryFn: async () => await fetchWorkspacesList(query),
        staleTime: 1000 * 60 * 5, // 5 minutes
        enabled: options?.enabled,
    });
}

async function fetchWorkspaces(params: GetWorkspacesQueryParams) {
    const response = await workspaceContractClient.getWorkspaces({
        query: params,
    });
    return {
        ...response.body,
        data: response.body.data.map(WorkspaceDetailsDto.deserialize),
    };
}

async function fetchWorkspaceDetails(workspaceId: string) {
    const response = await workspaceContractClient.getWorkspaceDetails({
        params: { workspaceId },
    });
    return WorkspaceDetailsDto.deserialize(response.body);
}

export function useClientsQuery(query: ClientsQueryParams) {
    const queryKey = workspaceQueryKeys.clientsQuery(query);

    return useQuery({
        queryKey,
        queryFn: async () => {
            if (!query.organizationId) {
                return { clients: [], total: 0, page: 0, pageCount: 0 };
            }

            const response = await workspaceContractClient.getClients({
                query,
            });

            return {
                ...response.body,
                clients: response.body.clients.map((client) =>
                    clientSchema.parse(client),
                ),
            };
        },
        placeholderData: keepPreviousDataOnPageChange(queryKey),
    });
}

// Loads every client in the org by walking all pages, rather than relying on a
// single capped page. Used where the full client set must be present (e.g. the
// apply-CoA-template enrichment), so large orgs (>500 clients) are not silently
// truncated.
export function useAllClientsForOrgQuery(organizationId: string) {
    return useQuery({
        queryKey: workspaceQueryKeys.allClientsForOrgQuery(organizationId),
        // Skip entirely without an org rather than running with an empty-string
        // id (which would only early-return an empty page anyway).
        enabled: !!organizationId,
        queryFn: async () => {
            // getClients is 0-based and caps the page size at 20 server-side, so
            // fetch page 0 first to learn pageCount, then load pages 1..N-1 in
            // parallel (independent — serializing would needlessly block the
            // drawer's enrichment for large orgs).
            const limit = 20;
            const firstPage = await workspaceContractClient.getClients({
                query: { organizationId, page: 0, limit },
            });
            // Tolerate per-page failures: a single transient error drops only
            // that page's clients (those rows fall back to blank enrichment)
            // rather than failing the whole query and blanking every client.
            const remainingResults = await Promise.allSettled(
                Array.from(
                    { length: Math.max(firstPage.body.pageCount - 1, 0) },
                    (_unused, index) =>
                        workspaceContractClient.getClients({
                            query: { organizationId, page: index + 1, limit },
                        }),
                ),
            );
            const rawClients = [
                ...firstPage.body.clients,
                ...remainingResults.flatMap((result) =>
                    result.status === "fulfilled"
                        ? result.value.body.clients
                        : [],
                ),
            ];
            return {
                clients: rawClients.map((client) => clientSchema.parse(client)),
            };
        },
    });
}

export function useClientsCloseOverviewSlimQuery(
    query: ClientsCloseOverviewSlimQuery,
) {
    const queryKey = workspaceQueryKeys.clientsCloseOverviewSlimQuery(query);

    return useQuery({
        queryKey,
        queryFn: async () => {
            if (!query.organizationId) {
                return {
                    workspaces: [],
                    total: 0,
                    page: 1,
                    pageCount: 0,
                };
            }

            const response =
                await workspaceContractClient.getClientsCloseOverviewSlim({
                    query,
                });

            return response.body;
        },
        staleTime: 1000 * 60 * 2, // 2 minutes
        placeholderData: keepPreviousDataOnPageChange(queryKey),
    });
}

export function useClientsCloseOverviewDetailsQuery(
    query: ClientsCloseOverviewDetailsQuery,
    options?: { enabled?: boolean },
) {
    return useQuery({
        queryKey: workspaceQueryKeys.clientsCloseOverviewDetailsQuery(query),
        queryFn: async () => {
            if (!query.organizationId || query.workspaceIds.length === 0) {
                return { workspaces: [] };
            }

            const response =
                await workspaceContractClient.getClientsCloseOverviewDetails({
                    query,
                });

            return response.body;
        },
        enabled: options?.enabled,
        staleTime: 1000 * 60 * 2, // 2 minutes
    });
}

export function useCloseUnreadCountsQuery(params: {
    organizationId: string;
    closeIds: string[];
}) {
    return useQuery({
        queryKey: workspaceQueryKeys.closeUnreadCountsQuery({
            organizationId: params.organizationId,
            closeIds: params.closeIds,
        }),
        queryFn: async () => {
            if (!params.organizationId || params.closeIds.length === 0) {
                return { counts: [] };
            }
            const response = await workspaceContractClient.getCloseUnreadCounts(
                {
                    query: {
                        organizationId: params.organizationId,
                        closeIds: params.closeIds,
                    },
                },
            );
            return response.body;
        },
        enabled: params.closeIds.length > 0 && !!params.organizationId,
        staleTime: 1000 * 60, // 1 minute — chat data changes frequently
    });
}

// I am used in the personal workspaces view => no org id passed down!
export function useBaseWorkspacesDetailsQuery(
    params: GetWorkspacesQueryParams,
) {
    const queryKey = workspaceQueryKeys.query(params);

    return useQuery({
        queryKey,
        queryFn: async () => await fetchWorkspaces(params),
        placeholderData: keepPreviousDataOnPageChange(queryKey),
    });
}

export const workspaceDetailsQueryOptions = (
    workspaceId: string | null | undefined,
) =>
    queryOptions({
        queryKey: workspaceQueryKeys.details(workspaceId ?? ""),
        queryFn: async () => {
            if (!workspaceId) {
                throw new Error("workspaceId is required");
            }
            return await fetchWorkspaceDetails(workspaceId);
        },
        enabled: !!workspaceId,
        staleTime: 1000 * 60 * 5, // 5 minutes
    });

export function useWorkspaceDetails(workspaceId: string | null | undefined) {
    return useQuery(workspaceDetailsQueryOptions(workspaceId));
}

const DEMO_DATA_SEEDING_POLL_INTERVAL_MS = 5_000;
const DEMO_DATA_SEEDING_USUAL_DURATION_MS = 10 * 60 * 1_000;

const isDemoDataSeeding = (workspace: Workspace | undefined) =>
    !!workspace?.demoDataSeedingStartedAt &&
    workspace.demoDataSeedingFinishedAt === null;

export function useDemoDataSeedingState(
    workspaceId: string | null | undefined,
) {
    const { data, dataUpdatedAt } = useQuery({
        ...workspaceDetailsQueryOptions(workspaceId),
        refetchInterval: (query) =>
            isDemoDataSeeding(query.state.data?.workspace)
                ? DEMO_DATA_SEEDING_POLL_INTERVAL_MS
                : false,
    });
    const seedingStartedAt = data?.workspace.demoDataSeedingStartedAt;
    const isSeeding = isDemoDataSeeding(data?.workspace);

    return {
        isSeeding,
        isTakingLongerThanUsual:
            isSeeding &&
            !!seedingStartedAt &&
            dataUpdatedAt - seedingStartedAt.getTime() >
                DEMO_DATA_SEEDING_USUAL_DURATION_MS,
    };
}

export function updateWorkspaceDetailsInQueryData(
    workspaceId: string,
    update: (
        oldWorkspaceDetails: WorkspaceDetails,
    ) => Omit<WorkspaceDetails, "workspace"> & { workspace: Workspace },
) {
    // Update regular workspace queries (exclude clients queries)
    queryClient.setQueriesData(
        {
            predicate: (query) => {
                const key = query.queryKey;
                return (
                    key[0] === "workspace" &&
                    key[1] === "queries" &&
                    key[2] !== "clients"
                );
            },
        },
        (oldData: ReturnType<typeof useBaseWorkspacesDetailsQuery>["data"]) => {
            if (!oldData || !oldData.data) {
                return oldData;
            }

            return {
                ...oldData,
                data: oldData.data.map((workspaceDetails) =>
                    workspaceDetails.workspace.id === workspaceId
                        ? update(workspaceDetails)
                        : workspaceDetails,
                ),
            };
        },
    );
}

export function updateClientInQueryData(
    clientId: string,
    update: (oldClient: ClientSchema) => ClientSchema,
) {
    // Update clients queries only
    queryClient.setQueriesData(
        {
            predicate: (query) => {
                const key = query.queryKey;
                return (
                    key[0] === "workspace" &&
                    key[1] === "queries" &&
                    key[2] === "clients"
                );
            },
        },
        (oldData: ReturnType<typeof useClientsQuery>["data"]) => {
            if (!oldData || !oldData.clients) {
                return oldData;
            }

            return {
                ...oldData,
                clients: oldData.clients.map((client) =>
                    client.id === clientId ? update(client) : client,
                ),
            };
        },
    );
}

export function useUpdateWorkspacePreferencesMutation(workspaceId: string) {
    return useMutation({
        mutationFn: (body: UpdateWorkspacePreferencesBody) =>
            workspaceContractClient.updateWorkspacePreferences({
                params: { workspaceId },
                body,
            }),
        onMutate: async (body) => {
            await queryClient.cancelQueries({
                queryKey: workspaceQueryKeys.details(workspaceId),
            });

            const previousData = queryClient.getQueryData(
                workspaceQueryKeys.details(workspaceId),
            );

            // `glFirstEnabled` is a boolean toggle, but the workspace stores a
            // `glFirstEnabledAt` timestamp. Translate it so the optimistic
            // workspace reflects the right field (a plain `...body` spread would
            // leave a stale `glFirstEnabledAt` and add a bogus `glFirstEnabled`).
            const { glFirstEnabled, ...restBody } = body;
            const workspacePatch =
                glFirstEnabled === undefined
                    ? restBody
                    : {
                          ...restBody,
                          glFirstEnabledAt: glFirstEnabled ? new Date() : null,
                      };

            queryClient.setQueryData(
                workspaceQueryKeys.details(workspaceId),
                (oldData: WorkspaceDetails | undefined) => {
                    if (!oldData) {
                        return oldData;
                    }

                    return {
                        ...oldData,
                        workspace: {
                            ...oldData.workspace,
                            ...workspacePatch,
                        },
                    };
                },
            );

            updateWorkspaceDetailsInQueryData(
                workspaceId,
                (workspaceDetails) => ({
                    ...workspaceDetails,
                    workspace: {
                        ...workspaceDetails.workspace,
                        ...workspacePatch,
                    },
                }),
            );

            return { previousData };
        },
        onError: (_error, _body, context) => {
            if (context?.previousData) {
                queryClient.setQueryData(
                    workspaceQueryKeys.details(workspaceId),
                    context.previousData,
                );
            }
        },
        onSettled: (_data, _error, body) => {
            queryClient.invalidateQueries({
                queryKey: workspaceQueryKeys.details(workspaceId),
            });
            queryClient.invalidateQueries({
                queryKey: workspaceQueryKeys.allQueries(),
            });

            if ("showAccountNumbers" in body) {
                queryClient.invalidateQueries({
                    queryKey: [accountingReportsQueryKeys.trialBalance],
                });
                queryClient.invalidateQueries({
                    queryKey: [accountingReportsQueryKeys.accountTransactions],
                });
            }
        },
    });
}
